import { css } from 'styled-components';

const baseFontSize = 16;
const NAVBAR_HEIGHT = '66px';

const pxToRem = (px) => px / baseFontSize;

const theme = {
  constants: {
    navbarHeight: NAVBAR_HEIGHT,
    viewer: {
      hintWidth: 340,
      sidebarWidth: 300,
      sidebarTooltipHeight: 118,
    },
  },
  colors: {
    transparent: 'transparent',
    black: '#171f2e',
    blackTwo: '#222222',
    trueBlack: '#000000',
    powderBlue: '#cfe3ec',
    silver: '#f6f6f7',
    white: '#ffffff',
    whiteTwo: '#ededed',
    whiteLilac: '#f9f9fa',
    whiteLilacTwo: '#f0f0f1',
    // green
    actionGreen: '#30ad7d',
    green: '#008000',
    brightGreen: '#7ebe3d',
    // yellow
    processingYellow: '#ecb252',
    // blue
    defaultActionBlue: '#23a2e0',
    defaultActionDarkBlue: '#1D8ABF',
    darkActionBlue: '#4eb5e5',
    activeBlue: '#145f85',
    pointerActionBlue: '#5ac7fc',
    mediumActionBlue: '#79c6eb',
    mediumActionBlueAlt: '#1777A4',
    mediumActionBlueTwo: '#2277D1',
    lightActionBlue: '#a6daf3;',
    extraLightActionBlue: '#d2ebf8',
    hoverButtonBlue: '#1475a6',
    darkBlue: '#1472a5',
    darkBlueTwo: '#1b5fa7',
    hoverBlue: '#004c97',
    lightBlue: '#4ba1db',
    lightBlueAlt: '#def1fb',
    oceanBlue: '#0075a4',
    facebookBlue: '#3a5898',
    twitterBlue: '#55acee',
    // navy
    defaultNavy: '#161e2f',
    darkNavy: '#434957',
    darkNavy2: '#393d45',
    mediumNavy: '#727682',
    lightNavy: '#a1a3ab',
    extraLightNavy: '#cfd1d4',
    mirage: '#171e2e',
    charade: '#2a2f39',
    // red
    alizarinCrimson: '#E43239',
    alertRed: '#f7f7f7',
    borderError: '#d1495a',
    fadedRed: '#d3495a',
    red: '#ff0000',
    darkRed: '#d0021b',
    errorRed: '#e23956',
    // gray
    border: '#999999',
    variationGray: '#8a8e97',
    licorice: '#333947',
    defaultGray: '#838b9b',
    darkGray: '#9ba2ae',
    darkGray2: '#8b8e96',
    darkGray3: '#343946',
    darkerGray2: '#555555',
    darkGrayButtonShadow: '#060C14',
    lightGraySplitSliderBackground: '#dbdbdb',
    mediumGray: '#b4b8c3',
    slateGray: '#788493',
    sidebarGray: '#d3d9df',
    mediumGrayTwo: '#b4b6c1',
    mediumGrayThree: '#979797',
    mediumGrayFour: '#a9adb5',
    mediumGrayFive: '#8a8e97',
    lightGray: '#ccd0d6',
    lightGrayTwo: '#f3f4f4',
    lightGrayThree: '#D3D3D3',
    lightGrayFour: '#D0D2D5',
    extraLightGray: '#e5e7ea',
    disabledGray: '#d6d7d9',
    grayFill: 'rgba(245, 245, 245, 0.5)',
    checkboxBorder: '#bfc1c7',
    choiceBorder: '#acb4c5',
    whiteSmoke: '#f6f5f5',
    mischka: '#afb3bb',
    titleGray: '#a4a6ab',
    darkGrayText: '#363c47',
    lightGrayText: '#a2a5ab',
    lightGrayTextTwo: '#b5b5b5',
    selectBorderGray: '#a5abb7',
    warningGray: '#737889',
    mediumGrayText: '#737882',
    buttonGrayText: '#3c424c',

    grayButtonNormalText: '#3C424D',
    grayButtonHoverBackground: '#DCDEE0',
    transparentMediumGrayBorder: 'rgba(151, 151, 151, 0.17)',
    transparentLightGrayBackground: 'rgba(216, 216, 216, 0.17)',
    transparentMediumGrayBackground: 'rgba(216, 216, 216, 0.50)',
    graySplitSliderShadow: 'rgba(0, 0, 0, 0.2)',
    graySplitSliderTicks: 'rgba(0, 0, 0, 0.2)',
    steel: '#a1aabc',
    zircon: '#ECEDEE',

    // features specific
    materialOrder: {
      navBar: {
        navbarBg: '#ffffff',
        defaultNavbarStepBg: '#e5e7ea',
        defaultNavbarStepColor: '#4d4d4d',
        defaultNavbarStepBorder: '#e5e7ea',
        disabledNavbarStepBg: '#b1dbf1',
        disabledNavbarStepColor: '#79c6eb',
        disabledNavbarStepBorder: '#b1dbf1',
        visitedNavbarStepBg: '#f0f2f5',
        visitedNavbarStepBorder: '#ccd0d6',
        activeNavbarStepBg: '#b1dbf1',
        activeNavbarStepColor: '#004c97',
        activeNavbarStepBorder: '#004c97',
      },
      qa: {
        headerBorder: '#dfdfdf',
        headerShadow: '0 10px 16px 0 rgba(0, 0, 0, 0.12)',
        footerShadow: '0 2px 4px 0 rgba(172, 172, 172, 0.59)',
        closeLinkColor: '#030303',
        inactiveColor: '#c7c7c7',
        separatorLine: '#979797',
      },
      searchBar: {
        border: '#e2e2e2',
        bg: 'rgba(246, 245, 245, 0.5)',
        activeBorder: '#1472a5',
        activeBg: '#ffffff',
      },
      search: {
        secondaryText: '#3c414c',
      },
      common: {
        dividerColor: '#979797',
        lightGrayBg: '#f0f2f5',
      },
      lineItem: {
        bg: '#f0f2f5',
      },
    },
    viewer: {
      sidebar: {
        headerText: '#000000',
        categoryBorder: '#d3d9df',
        countBox: '#d6d7d9',
        gray: '#8a8e97',
        thumbnailOverlay: 'rgba(22, 30, 47, 0.5)',
        hoverItemBackground: '#f9f9f9',
        selectedDarkBackground: '#000000',
        segmentCountBoxBorder: 'rgba(0, 0, 0, 0.08)',
      },
    },
    settings: {
      sidebar: {},
      integrations: {
        noImageColor: '#d8d8d8',
      },
      developer: {
        statusPending: '#ecb252',
        statusLive: '#7ebe3d',
      },
    },
  },
  transitions: {
    default: '100ms ease-in-out',
  },
  mixins: {
    font(weight, size, important) {
      return css`
        ${this.fontWeights[weight + (important ? 'Important' : '')]};
        font-size: ${pxToRem(size)}rem ${important ? ' !important' : ''};
      `;
    },
    textEllipsis() {
      return css`
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      `;
    },
    fontWeights: {
      light: css`
        font-family: 'Gotham Light', 'Helvetica Neue', 'Arial', sans-serif;
      `,
      book: css`
        font-family: 'Gotham Book', 'Helvetica Neue', 'Arial', sans-serif;
      `,
      medium: css`
        font-family: 'Gotham Medium', 'Helvetica Neue', 'Arial', sans-serif;
      `,
      bold: css`
        font-family: 'Gotham Bold', 'Helvetica Neue', 'Arial', sans-serif;
      `,
      boldImportant: css`
        font-family: 'Gotham Bold', 'Helvetica Neue', 'Arial', sans-serif !important;
      `,
      mediumImportant: css`
        font-family: 'Gotham Medium', 'Helvetica Neue', 'Arial', sans-serif !important;
      `,
      bookImportant: css`
        font-family: 'Gotham Book', 'Helvetica Neue', 'Arial', sans-serif !important;
      `,
    },
    focusBorder: css`
      box-shadow: 0 0 5px rgba(81, 203, 238, 1);
    `,
  },
  utils: {
    pxToRem,
  },
};

export default theme;
